import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { t, useLanguage } from "@/lib/i18n";

export function AlbumShareControls({ albumId }) {
  useLanguage();
  const [shares, setShares] = useState([]), [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [revoke, setRevoke] = useState(null);
  const load = async () => { const { data } = await api.get(`/albums/${albumId}/shares`); setShares(data.items); };
  useEffect(() => { load().catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail))); }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps
  const action = async (work) => {
    setBusy(true); setError("");
    try { await work(); await load(); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inviteMembers = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    const addresses = [...new Set(email.split(",").map((value) => value.trim().toLowerCase()).filter(Boolean))];
    const failed = [], errors = [];
    for (const address of addresses) {
      try { await api.post(`/albums/${albumId}/shares`, { email: address }); }
      catch (e) { failed.push(address); errors.push(`${address}: ${formatApiErrorDetail(e.response?.data?.detail)}`); }
    }
    setEmail(failed.join(", "));
    try { await load(); } catch (e) { errors.push(formatApiErrorDetail(e.response?.data?.detail)); }
    setError(errors.join("; ")); setBusy(false);
  };
  return <section className="rounded-xl border p-3 space-y-3" aria-label={t("Album sharing")}>
    <h2 className="font-semibold">{t("Share album")}</h2>
    <p className="text-sm text-stone-600">{t("Share immediately with a verified account by email. They can view and download the album; only you can change it.")}</p>
    <form className="flex flex-wrap items-end gap-2" onSubmit={inviteMembers}>
      <div><Label htmlFor={`share-email-${albumId}`}>{t("Member emails (separate with commas)")}</Label><Input id={`share-email-${albumId}`} type="email" multiple required value={email} maxLength={5000} disabled={busy} onChange={(e) => setEmail(e.target.value)} /></div>
      <Button disabled={busy || !email.trim()}>{t("Share album")}</Button>
    </form>
    {error && <p role="alert" className="text-red-800">{t(error)}</p>}
    <ul className="space-y-2">{shares.map((share) => <li key={share.recipient_id} className="flex flex-wrap items-center gap-2 text-sm border-t pt-2">
      <span className="flex-1 break-all">{share.recipient_name} · {share.recipient_email} · {t(share.status)}</span>
      {["pending", "accepted"].includes(share.status) && <Button variant="outline" size="sm" disabled={busy} onClick={() => setRevoke(share)}>{t("Revoke access")}</Button>}
    </li>)}</ul>
    <AlertDialog open={!!revoke} onOpenChange={(open) => { if (!open && !busy) setRevoke(null); }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t("Revoke access")}</AlertDialogTitle><AlertDialogDescription>{t("This member will no longer be able to view or download this album. Already downloaded files cannot be recalled.")}</AlertDialogDescription></AlertDialogHeader>
        <p className="break-all">{revoke?.recipient_email}</p>
        <AlertDialogFooter><AlertDialogCancel disabled={busy}>{t("Cancel")}</AlertDialogCancel><Button variant="destructive" disabled={busy} onClick={() => action(async () => { await api.delete(`/albums/${albumId}/shares/${revoke.recipient_id}`); setRevoke(null); })}>{t("Confirm revocation")}</Button></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>;
}

export function SharedAlbums({ onSelect, disabled, selectedId }) {
  useLanguage();
  const [shared, setShared] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(0), [error, setError] = useState("");
  const load = async () => {
    const albums = await api.get(`/shared-albums?offset=${page}&limit=24`);
    setShared(albums.data);
  };
  useEffect(() => { load().catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail))); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  return <section className="space-y-3 border-t pt-3" aria-label={t("Shared albums")}>
    <h2 className="font-semibold">{t("Shared with me")}</h2>
    <p className="text-xs text-stone-600">{t("Shared albums are available immediately for viewing and downloading only. They do not use your storage allowance.")}</p>
    {error && <p role="alert" className="text-red-800">{t(error)}</p>}
    {!shared.items.length && <p className="text-sm">{t("No shared albums yet.")}</p>}
    <div className="flex flex-wrap gap-2">{shared.items.map((album) => <Button key={album.id} variant={selectedId === album.id ? "default" : "outline"} aria-pressed={selectedId === album.id} className="h-auto whitespace-normal justify-start text-left" disabled={disabled} onClick={() => onSelect(album.id)}>{album.title} · {album.owner_name} · {t("View / download only")}</Button>)}</div>
    {(shared.total > 24 || page > 0) && <div className="flex gap-2"><Button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 24))}>{t("Previous")}</Button><Button disabled={page + 24 >= shared.total} onClick={() => setPage(page + 24)}>{t("Next")}</Button></div>}
  </section>;
}
